import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CanActivate } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';

import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { ConfirmationModal } from '@connect-services/client/components/confirmation-modal/confirmation-modal.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { EditModeLeaveService } from '@connect-services/client/services/edit-mode-leave.service';
import { SelectionService } from '@connect-services/client/services/map/selection/selection.service';
import { MapEditModeService } from '@connect-services/client/services/map-edit-mode.service';

@Injectable({
    providedIn: 'root',
})
export class LeaveEditModeOrSelectionGuard implements CanActivate {
    private readonly dialog = inject(MatDialog);
    private readonly editModeLeaveService = inject(EditModeLeaveService);
    private readonly mapEditModeService = inject(MapEditModeService);
    private readonly selectionService = inject(SelectionService);
    private readonly translocoService = inject(TranslocoService);

    public async canActivate(): Promise<boolean> {
        if (this.selectionService.isSelectionActive()) {
            return this.openLeaveSelectionModal();
        }

        if (!this.mapEditModeService.isMapEditModeActive()) {
            return true;
        }

        const leaveEditModeResult = await this.editModeLeaveService.requestLeaveEditMode();

        return leaveEditModeResult.canLeave;
    }

    private openLeaveSelectionModal(): Promise<boolean> {
        const dialogRef = this.dialog.open(ConfirmationModal, {
            autoFocus: false,
            data: {
                cancelLabel: this.translocoService.translate('cancel'),
                confirmLabel: this.translocoService.translate('quit'),
                description: this.translocoService.translate('leaveSelectionTool'),
                title: this.translocoService.translate('areYouSureToLeaveSelectionTool'),
                type: ModalType.DANGER,
            },
            disableClose: true,
            restoreFocus: false,
            width: '36rem',
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe((result: ModalResultEnum) => {
                resolve(result === ModalResultEnum.CONFIRM);
            });
        });
    }
}
